/**
 * Listening-session telemetry (reliability brief, Paul GO Oct 28 2026). OBSERVE ONLY: never issues a transport
 * command, never writes the store. It turns the playback state the UI already shows (plus the transport's own
 * diagnostic lines) into one start and one end event per listening session: start latency, each track end
 * (clean / early advance / skip-by-failure / user skip / repeat), Maestro-caused stops, failed starts, and the end
 * (clean / user_ended / maestro_stop / failed_start / app_closed). Works with every Connector version.
 *
 * #1650: a window that closes, a Mac that sleeps or a page that reloads ends the session as `app_closed`, a neutral
 * result (crash/freeze is decided server-side from the connector's own events); snapshot() lets the hook store the open
 * session so the next launch can send a lost end. A repeat counts only once the same track has stayed restarted for
 * REPEAT_CONFIRM_MS: a position reset followed by a track change is the track change.
 */
import {
  ADVANCE_REASON_WINDOW_MS, COUNT_KEY, REPEAT_CONFIRM_MS, REPEAT_RULE, SLEEP_GAP_MS, START_TIMEOUT_MS, STOP_CONFIRM_MS, PAUSE_END_MS,
  newSessionKey, zero,
  type CloseReason, type Counts, type PlaybackObservation, type SessionContext, type SessionOutcome, type TrackEndClass, type TrackerEmit, type TrackerSnapshot,
} from './listeningSessionTypes';

export * from './listeningSessionTypes';

const PLAYING = new Set(['playing']);
const STARTING = new Set(['buffering', 'loading']);
const NOT_PLAYING = new Set(['paused', 'stopped', 'idle', 'error']);

export class ListeningSessionTracker {
  private key: string | null = null;
  private openedAt = 0;
  private startLatency: number | null = null;
  private ctx: SessionContext | null = null;
  private counts: Counts = zero();
  private last: PlaybackObservation | null = null;
  private lastAdvanceReason: { reason: string; at: number } | null = null;
  private lastUserIntentAt = 0;
  private notPlayingSince: number | null = null;
  private notPlayingByUser = false;
  private stopCounted = false;
  private maxPosOnTrack = 0;
  private durOnTrack = 0;
  private lastSampleAt = 0;
  private pendingRepeat: { at: number; trackId: string; queueIndex: number } | null = null;

  get open(): boolean { return this.key != null; }
  /** A user asked to play something (playFrom / playAll / track pick). */
  noteUserPlayIntent(now: number): void { this.lastUserIntentAt = now; }
  /** QueueController's own "Auto-advance … reason" line. */
  noteAdvanceReason(reason: string, now: number): void { this.lastAdvanceReason = { reason, at: now }; }

  /** Observe one store/state sample. Returns any events to send. */
  observe(o: PlaybackObservation, ctx: () => SessionContext, now: number): TrackerEmit[] {
    const out: TrackEmitList = [];
    let prev = this.last;
    this.last = o;

    // The machine slept (or the page was frozen): the session ended when we last heard from it.
    if (this.key && this.lastSampleAt && now - this.lastSampleAt > SLEEP_GAP_MS) {
      this.end('app_closed', false, this.lastSampleAt, out, 'sleep');
      prev = null;
    }
    this.lastSampleAt = now;

    if (!this.key) {
      const starting = STARTING.has(o.playbackState) || PLAYING.has(o.playbackState);
      if (starting && o.trackId && (!prev || prev.playbackState !== o.playbackState || prev.trackId !== o.trackId)) {
        this.begin(ctx(), now, out);
        if (PLAYING.has(o.playbackState)) this.startLatency = this.latencyFrom(now);
      }
      return out;
    }

    if (this.startLatency == null && PLAYING.has(o.playbackState)) this.startLatency = this.latencyFrom(now);

    // Track change inside a session.
    if (prev && prev.trackId && o.trackId && prev.trackId !== o.trackId) {
      // A position reset seen just before this change was the change itself arriving early.
      this.pendingRepeat = null;
      this.countTrackEnd(this.classifyChange(prev, o, now));
      this.maxPosOnTrack = 0;
      this.durOnTrack = 0;
      this.stopCounted = false;
    } else if (prev && prev.trackId === o.trackId && PLAYING.has(o.playbackState)) {
      // Same track restarting from the top with nobody asking = a repeat CANDIDATE. It counts only if the
      // same track in the same queue slot is still playing near the top after REPEAT_CONFIRM_MS.
      if (!this.pendingRepeat && this.maxPosOnTrack >= 30 && o.position < 3 && o.duration > 0 && !o.userCommandRecent && o.repeatMode !== 'one'
        && prev.queueIndex === o.queueIndex && o.trackId) {
        this.pendingRepeat = { at: now, trackId: o.trackId, queueIndex: o.queueIndex };
      }
    }
    this.resolveRepeat(o, now);
    if (PLAYING.has(o.playbackState)) {
      this.maxPosOnTrack = Math.max(this.maxPosOnTrack, o.position || 0);
      this.durOnTrack = Math.max(this.durOnTrack, o.duration || 0);
    }

    // Failed start: no 'playing' within 30 s of opening.
    if (this.startLatency == null) {
      if (now - this.openedAt >= START_TIMEOUT_MS) this.end('failed_start', false, now, out);
      return out;
    }

    // Stopped / paused handling.
    if (NOT_PLAYING.has(o.playbackState)) {
      if (this.notPlayingSince == null) {
        this.notPlayingSince = now;
        // A pause is the listener's; only stopped/idle/error with no user command is a Maestro stop.
        this.notPlayingByUser = o.userCommandRecent || o.playbackState === 'paused';
      }
      const queueDone = o.queueIndex >= o.queueLength - 1 && (o.duration <= 0 || o.duration - o.position <= 15 || o.playbackState === 'stopped' || o.playbackState === 'idle');
      if (queueDone && !this.notPlayingByUser && (o.playbackState === 'stopped' || o.playbackState === 'idle')) {
        this.countTrackEnd('clean');
        this.end('clean', true, now, out);
        return out;
      }
      const quiet = now - this.notPlayingSince;
      if (this.notPlayingByUser) {
        if (quiet >= PAUSE_END_MS) this.end('user_ended', false, now, out);
      } else if (quiet >= STOP_CONFIRM_MS && !this.stopCounted) {
        this.stopCounted = true;
        this.countTrackEnd('maestro_stop');
        this.end('maestro_stop', false, now, out);
      }
    } else {
      this.notPlayingSince = null;
      this.notPlayingByUser = false;
    }
    return out;
  }

  /**
   * The window is going away (close, reload). The end is neutral `app_closed`: we cannot know why it closed.
   * Take snapshot() first and keep it stored: if this end never reaches the server, the next launch sends it.
   */
  unload(now: number): TrackerEmit[] {
    const out: TrackEmitList = [];
    if (!this.key) return out;
    this.end('app_closed', false, now, out, 'unload');
    return out;
  }

  /** The open session as plain data for localStorage, or null when none is open. */
  snapshot(now: number): TrackerSnapshot | null {
    if (!this.key) return null;
    return { v: 1, key: this.key, openedAt: this.openedAt, lastAt: now, startLatency: this.startLatency, ctx: this.ctx, counts: { ...this.counts } };
  }

  private resolveRepeat(o: PlaybackObservation, now: number): void {
    const p = this.pendingRepeat;
    if (!p) return;
    if (o.trackId !== p.trackId || o.queueIndex !== p.queueIndex || o.userCommandRecent || (!PLAYING.has(o.playbackState) && !STARTING.has(o.playbackState))) {
      this.pendingRepeat = null;
      return;
    }
    if (now - p.at >= REPEAT_CONFIRM_MS && PLAYING.has(o.playbackState) && o.position < this.maxPosOnTrack / 2) {
      this.pendingRepeat = null;
      this.countTrackEnd('repeat');
      this.maxPosOnTrack = 0;
    }
  }

  /** Periodic tick so timeouts fire even when the store is quiet. */
  tick(ctx: () => SessionContext, now: number): TrackerEmit[] {
    return this.last ? this.observe(this.last, ctx, now) : [];
  }

  private latencyFrom(now: number): number {
    // Anchor on the user's pick only when it is the pick that opened THIS session.
    const pick = this.lastUserIntentAt;
    const anchor = pick && pick <= this.openedAt && this.openedAt - pick <= 10_000 ? pick : this.openedAt;
    return Math.max(0, now - anchor);
  }

  private classifyChange(prev: PlaybackObservation, o: PlaybackObservation, now: number): TrackEndClass {
    const r = this.lastAdvanceReason && now - this.lastAdvanceReason.at <= ADVANCE_REASON_WINDOW_MS ? this.lastAdvanceReason.reason : null;
    if (r === 'failure') return 'skip_failure';
    if (r === 'skip' || o.userCommandRecent || (this.lastUserIntentAt && now - this.lastUserIntentAt <= ADVANCE_REASON_WINDOW_MS)) return 'user_skip';
    const dur = prev.duration || this.durOnTrack || 0;
    const reached = Math.max(this.maxPosOnTrack, prev.position || 0);
    if (dur > 0 && reached < dur * 0.5) return 'early_advance';
    return 'clean';
  }

  private countTrackEnd(c: TrackEndClass): void {
    this.counts[COUNT_KEY[c]] += 1;
  }

  private begin(ctx: SessionContext, now: number, out: TrackEmitList): void {
    this.key = newSessionKey(now);
    this.openedAt = now;
    this.startLatency = null;
    this.ctx = ctx;
    this.counts = zero();
    this.notPlayingSince = null;
    this.notPlayingByUser = false;
    this.stopCounted = false;
    this.maxPosOnTrack = 0;
    this.durOnTrack = 0;
    this.pendingRepeat = null;
    this.lastSampleAt = now;
    out.push({ type: 'listening_session_start', at: now, metadata: { session_key: this.key, ...ctx } });
  }

  private end(outcome: SessionOutcome, reachedEnd: boolean, now: number, out: TrackEmitList, closeReason?: CloseReason): void {
    if (!this.key) return;
    out.push({
      type: 'listening_session_end',
      at: now,
      metadata: {
        session_key: this.key,
        ...(this.ctx || {}),
        outcome,
        ...(closeReason ? { close_reason: closeReason, ended_at: now } : {}),
        repeat_rule: REPEAT_RULE,
        reached_end: reachedEnd,
        start_latency_ms: this.startLatency,
        duration_ms: now - this.openedAt,
        ...this.counts,
      },
    });
    this.key = null;
    this.ctx = null;
    this.pendingRepeat = null;
  }
}

type TrackEmitList = TrackerEmit[];
